/* eslint-disable max-lines */
import {get} from 'lodash-es';
import {RteMethodProps} from '@/rte';

import {isInKuShare} from '@/utils/urlUtils';
import {isDevelopment} from '@/config/env';
import {
    QASettingsData,
    SearchData,
    MemberCountType,
    IPublishListData,
    ICurDocCardLikesInfo,
} from '@/interface';
import {WizKnownError} from '@/sdk/common/error';
import {
    IAiChatPanel,
    IPublishSquareData,
    DocumentOperateData,
    MemberForApproval,
    MemberOperateData,
    GroupMemberForApproval,
    CreateRepo,
    DragOption,
    SidebarListObj,
    SidebarTreeObj,
    SidebarWidthChangeInfo,
    Follow,
    INewVersionInfo,
    ICommentCount,
    IRteToken,
} from '@/interface';
import {ICollaboratorSaveSuccess} from '@/types/document/notification';
import {DstTplDialogData} from '@/types/document/doc';
import {
    OpenType,
    OPEN_TAB,
    EDITOR_MODE,
    QAInitStatus,
    QuestionAnswerSwitch,
} from '@/Constant';
import {HistoryModeTabValue} from '@/constants/common';
import {CollaboratorList} from '@/types/document/collaborator';
import type {
    FileItem,
    ClientTransformFileData,
    UploadInputOptions,
    ExcelImportDialogData,
} from '@/types/document/file';
import {IPersonalHomeDocListTabMenu} from '@/types/personalHome/common';
import {
    AIAgentData,
    OptimizeObj,
} from '@/plugins/AIChatPlugin/AigcChat/AIAgent/interface';
import {IMaterialPosData} from '@/plugins/AIChatPlugin/AigcChat/Material/interface';
import type {IDropMenuToolItem} from '@/kuTools/sdk/hooks/useDropMenuTool';

import store from './simple_store';
import {KEYS as STORE_KEYS} from './constant';
import {IPageSecurityWithWaterMark} from '@/components/WaterMark';

// 仅用于线上定位问题，不可在代码里使用
(window as any).__ku_wiki_store__ = store;

export {connect, connectStore} from './simple_store';

export const getData = store.getData;
export const setData = store.setData;
// 订阅变更
export const subscribe = (key: string, listener: (...args: any[]) => void) => {
    store.subscribe(key, listener);
};
// 取消订阅
export const unsubscribe = (
    key: string,
    listener: (...args: any[]) => void
) => {
    store.unsubscribe(key, listener);
};

export const KEYS = STORE_KEYS;

export function setCurDocToken(data: IRteToken) {
    store.setData(KEYS.CUR_DOCUMENT_TOKEN, data);
}

export function setOpenSettingStatus(visible: boolean, openTab?: OpenType) {
    store.setData(KEYS.OPEN_SETTING, {
        visible,
        openTabMsg: openTab || OPEN_TAB.openTabMsg,
    });
}
/**
 * 避免多次请求接口，用store共享变量
 * @param {boolean}isCoin 当前用户对当前文档是否投币
 */
export function setIsDepositKnowledgeCoin(isCoin: boolean) {
    store.setData(KEYS.CUR_USER_IS_DEPOSIT_CUR_DOCUMENT, isCoin);
}
export function setCurMeetingDocs(meetingDocs: string[]) {
    store.setData(KEYS.CUR_MEETING_DOCS, meetingDocs);
}

export function setRwMode(val: boolean) {
    store.setData(KEYS.RW_MODE, !!val);
}

export function getRwMode() {
    return !!store.getData(KEYS.RW_MODE);
}

export function setQueryInfo(data: any) {
    store.setData(KEYS.QUERY_INFO, data);
}

export function setSidebarMenuTarget(target: any) {
    store.setData(KEYS.SIDEBAR_MENU_TARGET, target);
}

export function setHistoryModeTabValue(val: HistoryModeTabValue) {
    store.setData(KEYS.HISTORY_MODE_TAB_VALUE, val);
}

export function setHistoryModeVersionId(val: number | null) {
    store.setData(KEYS.HISTORY_MODE_VERSION_ID, val);
}

export function setCurDocumentError(
    error: WizKnownError | Error | unknown | null
) {
    // WizKnownError 不能存入store 会引起cloneDeep错误返回{} 加此兼容
    if (error instanceof WizKnownError) {
        const message = get(error, 'origin.result', '');
        error = {
            code: error.code,
            message:
                typeof message === 'string' ? message : JSON.stringify(message),
        };
    }
    store.setData(KEYS.CUR_DOCUMENT_ERROR, error);
}

export function getCurDocumentError() {
    return store.getData(KEYS.CUR_DOCUMENT_ERROR);
}

export function setCurRepositoryError(
    error: WizKnownError | Error | unknown | null
) {
    // WizKnownError 不能存入store 会引起cloneDeep错误返回{} 加此兼容
    if (error instanceof WizKnownError) {
        const message = get(error, 'origin.result', '');
        error = {
            code: error.code,
            message:
                typeof message === 'string' ? message : JSON.stringify(message),
        };
    }
    store.setData(KEYS.CUR_REPOSITORY_ERROR, error);
}
export function setShowEditorChildren(show: boolean) {
    store.setData(KEYS.SHOW_EDITOR_CHILDREN, show);
}

export function getShowEditorChildren() {
    return store.getData(KEYS.SHOW_EDITOR_CHILDREN);
}

export function setShowPublishDocDialog(show: boolean) {
    store.setData(KEYS.SHOW_PUBLISH_DOC_DIALOG, show);
}

export function setPublishDocDialogProps(props?: {
    guideType?: number;
    sceneInfo?: IPublishListData[];
}) {
    store.setData(KEYS.PUBLISH_DOC_DIALOG_PROPS, props);
}

export function getPublishDocDialogProps() {
    return store.getData(KEYS.PUBLISH_DOC_DIALOG_PROPS);
}

export function setShowSaveTplDialog(show: boolean) {
    store.setData(KEYS.SHOW_SAVE_TPL_DIALOG, show);
}
export function setIsWideScreenMode(isWideScreen: boolean) {
    store.setData(KEYS.IS_WIDE_SCREEN_MODE, isWideScreen);
}
export function setShowDocStatDialog(show: boolean, wordNumber?: number) {
    store.setData(KEYS.SHOW_DOC_STAT_DIALOG, {show, wordNumber});
}

export function getCurCommentCount() {
    return store.getData(KEYS.CUR_DOCUMENT_COMMENT_COUNT);
}

export function setCurCommentCount(commentCount: ICommentCount) {
    store.setData(KEYS.CUR_DOCUMENT_COMMENT_COUNT, commentCount);
}

export function setCurCollaborators(collaborators: CollaboratorList[]) {
    store.setData(KEYS.CUR_DOCUMENT_COLLABORATORS, collaborators);
}

export function setCurNewVersionInfo(info: INewVersionInfo | null) {
    store.setData(KEYS.CUR_DOCUMENT_NEW_VERSION_INFO, info);
}
export function getCurNewVersionInfo() {
    return store.getData(KEYS.CUR_DOCUMENT_NEW_VERSION_INFO) || {};
}

export function setCurDocStar(starInfo: Follow['followGuid'] | null) {
    store.setData(KEYS.CUR_DOCUMENT_STAR, starInfo);
}

export function getCurDocKnowledgeCoin() {
    return store.getData(KEYS.CUR_DOCUMENT_KNOWLEDGE_COIN);
}

export function setCurDocKnowledgeCoin(knowledgeCoinCount: number) {
    store.setData(KEYS.CUR_DOCUMENT_KNOWLEDGE_COIN, knowledgeCoinCount);
}

export function setEditorRef(ref: React.RefObject<RteMethodProps | null>) {
    store.setData(KEYS.EDITOR_REF, {ref, timeStamp: Date.now()});
}

export function getEditorRef() {
    const data = store.data[KEYS.EDITOR_REF];
    if (data) {
        return data.ref as React.RefObject<RteMethodProps | null>;
    }
    return {current: null};
}

export function setCommentEditorRef(ref: React.RefObject<any | null>) {
    store.setData(KEYS.COMMENT_EDITOR_REF, {ref, timeStamp: Date.now()});
}

export function getCommentEditorRef() {
    const data = store.data[KEYS.COMMENT_EDITOR_REF];
    if (data) {
        return data.ref as React.RefObject<any | null>;
    }
    return {current: null};
}

export function setPPTEditorRef(ref: React.RefObject<any | null>) {
    store.setData(KEYS.PPT_EDITOR_REF, {ref, timeStamp: Date.now()});
}

export function getPPTEditorRef() {
    const data = store.data[KEYS.PPT_EDITOR_REF];
    if (data) {
        return data.ref as React.RefObject<any | null>;
    }
    return {current: null};
}

export function getCurDocStar() {
    return store.getData(KEYS.CUR_DOCUMENT_STAR);
}

export function setCurDocTag(data: any) {
    const prevData = store.getData(KEYS.CUR_DOCUMENT_TAG);
    store.setData(KEYS.CUR_DOCUMENT_TAG, {
        ...prevData,
        ...data,
    });
}

export function setPageSecuritySettings(
    settings: IPageSecurityWithWaterMark | undefined
) {
    store.setData(KEYS.PAGE_SECURITY_SETTINGS, settings);
}
export function getPageSecuritySettings() {
    return store.getData(KEYS.PAGE_SECURITY_SETTINGS);
}

export function setKuSecuritySettings(
    settings: IPageSecurityWithWaterMark | undefined
) {
    store.setData(KEYS.KU_SECURITY_SETTINGS, settings);
}
export function getKuSecuritySettings() {
    return store.getData(KEYS.KU_SECURITY_SETTINGS);
}

export function setRepositoryTemplate(template: any) {
    store.setData(KEYS.TEMPLATE, template);
}

export function setShowCreateRepo(createInfo: CreateRepo) {
    store.setData(KEYS.SHOW_CREATE_REPO, createInfo);
}

export function getShowCreateRepo() {
    return store.getData(KEYS.SHOW_CREATE_REPO);
}

export function setDepIntroDetail(visible: boolean) {
    store.setData(KEYS.OPEN_DEP_INTRO_DETAIL, visible);
}

export function setSidebarOpen(open: boolean) {
    store.setData(KEYS.SIDE_NAR_OPEN, open);
}

export function getSidebarOpen() {
    return store.getData(KEYS.SIDE_NAR_OPEN);
}
export function setSidebarDragOption(dragOption: DragOption) {
    return store.setData(KEYS.SIDE_BAR_DRAG_OPTION, dragOption);
}

export function getSidebarDragOption() {
    return store.getData(KEYS.SIDE_BAR_DRAG_OPTION);
}

export function setCurDepartment(department: Department | undefined | null) {
    store.setData(KEYS.CUR_DEPARTMENT, department);
}

export function setCurDepartmentError(err: any) {
    store.setData(KEYS.CUR_DEPARTMENT_ERROR, err);
}

export function getCurDepartment() {
    return store.getData(KEYS.CUR_DEPARTMENT);
}

export function setSidebarWidthChangeInfo(
    sidebarWidthChangeInfo: SidebarWidthChangeInfo
) {
    store.setData(KEYS.SIDEBAR_WIDTH_CHANGE_INFO, sidebarWidthChangeInfo);
}

export function getSidebarWidth() {
    const width = store.getData(KEYS.SIDEBAR_WIDTH_CHANGE_INFO);
    return width;
}

interface UserWithAchieve extends User {
    showAchieve?: boolean;
}

export function getUserInfo(userGuid: string) {
    const map: Map<string, UserWithAchieve> = store.getData(KEYS.USERS_MAP);
    if (!map) {
        return null;
    }
    return map.get(userGuid) || null;
}

export function setUserInfo(user: User) {
    const map = store.getData(KEYS.USERS_MAP) || new Map<string, User>();
    map.set(user.userGuid, user);
    store.setData(KEYS.USERS_MAP, map);
}

export function setUserInfoBatch(users: User[], showAchieve?: boolean) {
    const map = store.getData(KEYS.USERS_MAP) || new Map<string, User>();
    users.forEach((user: User) => {
        map.set(user.userGuid, {
            ...user,
            showAchieve: !!showAchieve,
        });
    });
    store.setData(KEYS.USERS_MAP, map);
}

export function setCurRepositoryName(repositoryName: string) {
    store.setData(KEYS.CUR_REPOSITORY_NAME, repositoryName);
}

export function getCurRepository() {
    const repository = store.getData(KEYS.CUR_REPOSITORY);
    if (!repository) {
        return null;
    }
    return repository as Repository;
}

export function setCurRepositoryScope(scope: number) {
    const repo = getCurRepository();
    store.setData(KEYS.CUR_REPOSITORY, {...repo, scope});
}

export function setCurRepository(repository: Repository | null) {
    store.setData(KEYS.CUR_REPOSITORY, repository);
}

export function setCurPage(
    page: string,
    repositoryGuid: string | undefined,
    docGuid?: string
) {
    store.setData(KEYS.CUR_PAGE, {page, repositoryGuid, docGuid});
}

export function getCurPage() {
    return store.getData(KEYS.CUR_PAGE);
}

export function setCurDocument(doc: WizDocument | null) {
    store.setData(KEYS.CUR_DOCUMENT, doc);
}

export function getCurDocument() {
    const curDocument = store.getData(KEYS.CUR_DOCUMENT);
    if (!curDocument) {
        return null;
    }
    return curDocument as WizDocument;
}

export function getCurDocuments() {
    return store.getData(KEYS.CUR_DOCUMENTS);
}

export function setCurDocuments(docList: WizDocument[]) {
    store.setData(KEYS.CUR_DOCUMENTS, docList);
}

export function setDocsInMeeting(docs: string[]) {
    store.setData(KEYS.DOCS_IN_MEETING, docs);
}

export function setApprovalOperate(
    data: MemberForApproval | GroupMemberForApproval
) {
    store.setData(KEYS.APPROVAL_OPERATE, data);
}

export function setDocumentOperate(data: DocumentOperateData) {
    data.timestamp = Date.now();
    store.setData(KEYS.DOCUMENT_OPERATE, data);
}

export function setCreateDocument({
    docType,
    bookType,
    parentGuid,
    openInNewTab,
    createPersonPage,
    templateId,
}: {
    docType: number;
    bookType: number;
    parentGuid: string | null;
    openInNewTab?: boolean;
    createPersonPage?: boolean;
    templateId?: string;
}) {
    store.setData(KEYS.CREATE_DOCUMENT, {
        docType,
        bookType,
        parentGuid,
        openInNewTab,
        timestamp: Date.now(),
        createPersonPage,
        templateId,
    });
}

export function setCreateDirectory(
    parentGuid: string | null,
    feLogNumber: string,
    openInNewTab?: boolean,
    createPersonPage?: boolean,
    inherit?: number,
    scope?: number
) {
    store.setData(KEYS.CREATE_DIRECTORY, {
        parentGuid,
        feLogNumber,
        openInNewTab,
        timestamp: Date.now(),
        createPersonPage,
        inherit,
        scope,
    });
}

export function setDeleteDocument(docGuid: string, doc?: WizDocument) {
    store.setData(KEYS.DELETE_DOCUMENT, {docGuid, doc, timestamp: Date.now()});
}

export function setMoveDocument(docGuid: string, isBatch: boolean = false) {
    store.setData(KEYS.MOVE_DOCUMENT, {docGuid, timestamp: Date.now(), isBatch});
}

// eslint-disable-next-line max-len
export function setCopyDocument(
    type: string | number,
    docGuid: string,
    title: string,
    isSidebar: boolean,
    hasChild?: boolean,
    isBatch?: boolean
) {
    store.setData(KEYS.COPY_DOCUMENT, {
        type,
        docGuid,
        title,
        isSidebar,
        hasChild,
        timestamp: Date.now(),
        isBatch: isBatch || false,
    });
}

export function setConvertToCatalogDocument(docGuid: string, inherit?: number, scope?: number, isPop?: boolean) {
    store.setData(KEYS.CONVERTTOCATALOG_DOCUMENT, {
        docGuid,
        timestamp: Date.now(),
        inherit,
        scope,
        isPop,
    });
}

export function setConvertToDocDocument(docGuid: string) {
    store.setData(KEYS.CONVERTTODOC_DOCUMENT, {docGuid, timestamp: Date.now()});
}

export function setMemberOperate(data: MemberOperateData) {
    store.setData(KEYS.MEMBER_OPERATE, data);
}

export function setDocumentMode(mode: EDITOR_MODE) {
    // TODO: 读写合一小流量适配
    const rwMode = getRwMode();
    if (
        rwMode &&
        [EDITOR_MODE.preview, EDITOR_MODE.edit, EDITOR_MODE.meeting].includes(
            mode
        )
    ) {
        mode = EDITOR_MODE.normal;
    }
    store.setData(KEYS.DOCUMENT_MODE, mode);
}

export function getDocumentMode() {
    return store.getData(KEYS.DOCUMENT_MODE);
}

export function setPreviewImage(val: any) {
    return store.setData(KEYS.PREVIEW_IMAGE, val);
}
export function setPreviewImageVisible(val: boolean) {
    return store.setData(KEYS.PREVIEW_IMAG_VISIBLE, val);
}
export function setShowExternalSharingDialog(val: boolean) {
    return store.setData(KEYS.SHOW_EXTERNAL_SHARING_DIALOG, val);
}
export function setPreviewImageRatio(val: number) {
    return store.setData(KEYS.PREVIEW_IMAGE_RATIO, val);
}
export function setPreviewImageAdaptionRatio(val: number) {
    return store.setData(KEYS.PREVIEW_IMAGE_ADAPTION_RATIO, val);
}
export function getPreviewImageAdaptionRatio() {
    return store.getData(KEYS.PREVIEW_IMAGE_ADAPTION_RATIO);
}
export function setPreviewImageMode(val: string) {
    return store.setData(KEYS.PREVIEW_IMAGE_MODE, val);
}

export function setDemoMode(val: boolean) {
    store.setData(KEYS.DEMO_MODE, val);
}

export function getDemoMode() {
    store.getData(KEYS.DEMO_MODE);
}

export function setEditorDomReady(isReady: boolean) {
    store.setData(KEYS.EDITOR_DOM_READY, isReady);
}

export function setDocumentPosition(data: any) {
    console.info('trigger set document scroll position, position:', data); // 用于线上调试定位，勿删
    store.setData(KEYS.DOCUMENT_POSITION, data);
}

// 新手引导相关
export function setShareCollaborateGuide(data: any) {
    store.setData(KEYS.SHARE_COLLABORATE_GUIDE, data);
}
export function setNewGuideData(data: any) {
    store.setData(KEYS.NEW_GUIDE, data);
}

export function setDoneEndMeetingGuide(data: boolean) {
    store.setData(KEYS.DONE_END_MEETING_GUIDE, data);
}
export function setDoneSettingGuide(data: boolean) {
    store.setData(KEYS.DONE_SETTING_GUIDE, data);
}

export function setStatNumShow(visible: boolean) {
    store.setData(KEYS.IS_STAT_NUM_SHOW, visible);
}

export function setDocSaveStatus(status: string) {
    store.setData(KEYS.DOCUMENT_SAVE_STATUS, status);
}

export function setDocWsStatus(status: string) {
    store.setData(KEYS.DOCUMENT_WS_STATUS, status);
}

export function setLatestEditUser(data: ICollaboratorSaveSuccess['data']) {
    store.setData(KEYS.LATEST_EDIT_USER, {...data, timeStamp: Date.now()});
}

export function setUser(data: User) {
    store.setData(KEYS.USER, data);
}

export function setIsBaikeHome(val: boolean) {
    store.setData(KEYS.IS_BAIKE_HOME, val);
}

export function setIsBaikeRepo(val: boolean) {
    store.setData(KEYS.IS_BAIKE_REPO, val);
}

export function setIsBaikeRoute(val: boolean) {
    store.setData(KEYS.IS_BAIKE_ROUTE, val);
}

export function setCommonDocs(docs: WizDocument[]) {
    store.setData(KEYS.COMMON_DOCS, docs);
}

export function setTodoConflictList(list: any[]) {
    store.setData(KEYS.TODO_CONFLICT_LIST, list);
}

export function setShowOfficialTag(val: boolean) {
    store.setData(KEYS.SHOW_OFFICIAL_TAG, val);
}

export function setPublishToSquareData(data: IPublishSquareData) {
    const oldData = store.getData(KEYS.PUBLISH_TO_SQUARE_DATA);
    store.setData(KEYS.PUBLISH_TO_SQUARE_DATA, {...oldData, ...data});
}

export function getPublishToSquareData() {
    return store.getData(KEYS.PUBLISH_TO_SQUARE_DATA);
}

export function setICafeRelatedDialog(
    visible: boolean,
    type?: 'menu' | 'card'
) {
    store.setData(KEYS.ICAFE_RELATED_SHOW, {
        visible,
        openSource: type,
    });
}

export function setIsDocCiteShow(status: boolean) {
    store.setData(KEYS.IS_DOC_CITE_SHOW, status);
}

export function setIsKuShareMode(val?: string) {
    store.setData(KEYS.IS_KU_SHARE_MODE, val);
}

export function setKuShareParams(val: any) {
    store.setData(KEYS.KU_SHARE_PARAMS, val);
}

export function setKuShareHostInfo(val: any) {
    store.setData(KEYS.KU_SHARE_HOST_INFO, val);
}

export function setKuShareFullscreen(val: boolean) {
    store.setData(KEYS.KU_SHARE_FULLSCREEN, val);
}

export function setDirectoryEdit(data: string[]) {
    store.setData(KEYS.DIRECTORY_EDIT, data);
}

export function getDirectoryEdit() {
    return store.getData(KEYS.DIRECTORY_EDIT);
}

export function setDirectoryhasData(data: boolean) {
    store.setData(KEYS.DIRECTORY_HAS_DATA, data);
}

export function getDirectoryhasData() {
    return store.getData(KEYS.DIRECTORY_HAS_DATA);
}

export function setInviteMember() {
    return store.setData(KEYS.INVITE_MEMBER_TIME, Date.now());
}

export function getInviteMember() {
    return store.getData(KEYS.INVITE_MEMBER_TIME);
}

export function setDocContentWidth(data: {width: number, left: number}) {
    return store.setData(KEYS.DOC_CONTENT_WIDTH, data);
}

export function getHeaderAdapterData() {
    return store.getData(KEYS.HEADER_ADAPTER_DATA);
}

export function setHeaderAdapterData(data: any) {
    return store.setData(KEYS.HEADER_ADAPTER_DATA, data);
}

export function setIsNeedRefreshICafe(val: boolean) {
    store.setData(KEYS.IS_NEED_REFRESH_ICAFE, val);
}

export function getIsNeedRefreshICafe() {
    const isNeedRefresh = store.getData(KEYS.IS_NEED_REFRESH_ICAFE);
    if (isNeedRefresh === undefined) {
        return false;
    }
    return isNeedRefresh;
}

export function getImportFileList(): FileItem[] {
    return store.getData(KEYS.IMPORT_FILE_LIST) || [];
}

export function setImportFileList(list: FileItem[]) {
    store.setData(KEYS.IMPORT_FILE_LIST, list);
}

// 增加导入文件(支持多个)
export function addImportFiles(files: FileItem[]) {
    const importFileList: FileItem[] =
        store.getData(KEYS.IMPORT_FILE_LIST) || [];

    const fileNum = importFileList.length;
    files.forEach((file: FileItem, index: number) => {
        file.index = fileNum + index; // 增加文件时加上文件索引值
        importFileList.push({...file});
    });

    store.setData(KEYS.IMPORT_FILE_LIST, importFileList);
}

// 更新单个导入文件
export function setImportFile(file: FileItem) {
    // 由于关闭弹窗后，清空的文件可能还在上传
    // 根据文件清除标记，避免清空文件后仍会更新状态
    if (file.isClear) {
        return;
    }

    const {index} = file;
    const importFileList: FileItem[] = store.getData(KEYS.IMPORT_FILE_LIST);

    // 避免清空文件后仍会更新状态
    if (!importFileList[index as number]) {
        return;
    }

    importFileList[index as number] = {
        ...importFileList[index as number],
        ...file,
    };
    store.setData(KEYS.IMPORT_FILE_LIST, importFileList);
}

export function getClientImportFileData(): ClientTransformFileData {
    return store.getData(KEYS.CLIENT_IMPORT_FILE_DATA) || {};
}

export function setClientImportFileData(data: ClientTransformFileData) {
    store.setData(KEYS.CLIENT_IMPORT_FILE_DATA, data);
}

export function getClientTransformFileData(): ClientTransformFileData {
    return store.getData(KEYS.CLIENT_TRANSFORM_FILE_DATA) || {};
}

export function setClientTransformFileData(data: ClientTransformFileData) {
    store.setData(KEYS.CLIENT_TRANSFORM_FILE_DATA, data);
}

export function setUploadInputOptions(val: UploadInputOptions) {
    store.setData(KEYS.UPLOAD_INPUT_OPTIONS, {
        key: +new Date(), // 强制更新，跳过全相等判断
        ...val,
    });
}

export function setDocReady(data: boolean) {
    store.setData(KEYS.DOC_READY, data);
}

export function getDocReady() {
    const docReady = store.getData(KEYS.DOC_READY);
    if (docReady === undefined) {
        return false;
    }
    return docReady;
}

export function setUrlQuery(queryData: any) {
    store.setData(KEYS.URL_QUERY, queryData);
}

export function getUrlQuery() {
    return store.getData(KEYS.URL_QUERY) || {};
}

export function setMessageUnReadCount(data: any) {
    store.setData(KEYS.MESSAGE_UNREAD_COUNT, data);
}

export function setInMessageOptimizeWhiteList(data: boolean) {
    store.setData(KEYS.IN_MESSAGE_OPTIMIZE_WHITE_LIST, data);
}

export function getInMessageOptimizeWhiteList() {
    return store.getData(KEYS.IN_MESSAGE_OPTIMIZE_WHITE_LIST);
}

export function setDstTplDialogData(data: DstTplDialogData) {
    store.setData(KEYS.DST_TPL_DIALOG_DATA, data);
}

export function setDstToken(val: string) {
    store.setData(KEYS.DST_TOKEN, val);
}

export function setCreateDstLoading(val: boolean) {
    store.setData(KEYS.CREATE_DST_LOADING, val);
}

export function setPersonalHomeUserGuid(val: string) {
    store.setData(KEYS.PERSONAL_HOME_USER_GUID, val);
}

export function setPersonalHomeUserName(val: string) {
    store.setData(KEYS.PERSONAL_HOME_USER_NAME, val);
}

export function setPersonalHomeStarHasGiven(val: any) {
    store.setData(KEYS.PERSONAL_HOME_STAR_HAS_GIVEN, val);
}
export function setPersonalHomeCoinInfo(val: any) {
    store.setData(KEYS.PERSONAL_HOME_COIN_INFO, val);
}

export function getPersonalHomeCoinInfo() {
    return store.getData(KEYS.PERSONAL_HOME_COIN_INFO);
}

export function setPersonalHomeSubInfo(val: any) {
    store.setData(KEYS.PERSONAL_HOME_SUB_INFO, val);
}

export function getPersonalHomeSubInfo() {
    return store.getData(KEYS.PERSONAL_HOME_SUB_INFO) || {};
}

export function setPersonalHomeBg(bg: string) {
    return store.setData(KEYS.PERSONAL_HOME_BG, bg);
}

export function setIsOpenAigc(val: boolean) {
    store.setData(KEYS.IS_OPEN_AIGC, val);
}

export function getIsOpenAigc() {
    return store.getData(KEYS.IS_OPEN_AIGC);
}

export function setImportExcelDialogData(data: ExcelImportDialogData) {
    store.setData(KEYS.IMPORT_EXCEL_DIALOG_DATA, data);
}

export function setSidebarListObj(sidebarListObj: SidebarListObj) {
    store.setData(KEYS.SIDEBAR_LIST_OBJ, sidebarListObj);
}

export function getSidebarListObj() {
    return store.getData(KEYS.SIDEBAR_LIST_OBJ) as SidebarListObj;
}

export function setSidebarTreeObj(sidebarTreeObj: SidebarTreeObj) {
    store.setData(KEYS.SIDEBAR_TREE_OBJ, sidebarTreeObj);
}

export function getSidebarTreeObj() {
    return store.getData(KEYS.SIDEBAR_TREE_OBJ) as SidebarTreeObj;
}

export function setAiChatPanel(aiChatPanel?: Partial<IAiChatPanel>) {
    store.setData(KEYS.AI_CHAT_PANEL, aiChatPanel);
}

export function setAIAgentData(data: AIAgentData) {
    return store.setData(KEYS.AI_AGENT_DATA, data);
}

export function getAIAgentData(): AIAgentData {
    return store.data[KEYS.AI_AGENT_DATA];
}

export function getAiChatPanel() {
    return store.getData(KEYS.AI_CHAT_PANEL) || {};
}

export function setPersonalHomeDocListTabMenu(
    val: IPersonalHomeDocListTabMenu
) {
    store.setData(KEYS.PERSONAL_HOME_DOC_LIST_TAB_MENU, val);
}

export function getPersonalHomeDocListTabMenu() {
    return store.getData(KEYS.PERSONAL_HOME_DOC_LIST_TAB_MENU);
}

export function setIsDocEmpty(val: boolean) {
    store.setData(KEYS.IS_DOC_EMPTY, val);
}

export function getIsDocEmpty() {
    return store.getData(KEYS.IS_DOC_EMPTY);
}

export function setIsAiTemplateActive(val: boolean) {
    store.setData(KEYS.IS_AI_TEMPLATE_ACTIVE, val);
}

export function getIsAiTemplateActive() {
    return store.getData(KEYS.IS_AI_TEMPLATE_ACTIVE);
}

export function setAiTemplateList(val: boolean) {
    store.setData(KEYS.AI_TEMPLATE_LIST, val);
}

export function setQuestionAnswerSwitch(val: QuestionAnswerSwitch | null) {
    store.setData(KEYS.IS_OPEN_QUESTION_ANSWER, val);
}

export function getQuestionAnswerSwitch() {
    return store.getData(KEYS.IS_OPEN_QUESTION_ANSWER);
}

export function setQuestionAnswerIsInGray(val: boolean) {
    store.setData(KEYS.IS_OPEN_QUESTION_ANSWER_IN_GRAY, val);
}

export function getQuestionAnswerIsInGray() {
    return store.getData(KEYS.IS_OPEN_QUESTION_ANSWER_IN_GRAY);
}

export function setIsCardView(val: boolean) {
    store.setData(KEYS.IS_CARD_VIEW, val, true);
}

export function setIsViewModified(val: boolean) {
    store.setData(KEYS.IS_VIEW_MODIFIED, val, true);
}

export function setShowSlide(val: boolean) {
    store.setData(KEYS.SHOW_SLIDE, val);
}

export function setQASettings(val: QASettingsData | null) {
    store.setData(KEYS.QUESTION_ANSWER_SETTINGS, val);
}

export function getQASettings() {
    return store.getData(KEYS.QUESTION_ANSWER_SETTINGS);
}

export function setMemberCount(count: MemberCountType | null) {
    const computerCount = {...store.getData(KEYS.MEMBERS_COUNT), ...count};
    store.setData(KEYS.MEMBERS_COUNT, computerCount);
}

export function getMemberCount() {
    return store.getData(KEYS.MEMBERS_COUNT);
}
export function setPowerRecyclePermission(val: any) {
    store.setData(KEYS.POWER_RECYCLE_PERMISSION, val);
}

export function getPowerRecyclePermission() {
    return store.getData(KEYS.POWER_RECYCLE_PERMISSION);
}

export function getRepoPermissionRiskCount() {
    return store.getData(KEYS.REPO_PERMISSION_RISK_COUNT);
}

export function setRepoPermissionRiskCount(val: any) {
    store.setData(KEYS.REPO_PERMISSION_RISK_COUNT, val);
}

export function setIsShowCover(val: boolean) {
    store.setData(KEYS.IS_SHOW_COVER, val);
}

export function setShowDocCover(val: boolean) {
    store.setData(KEYS.SHOW_DOC_COVER, val);
}

export function setShowSlideCover(val: boolean) {
    store.setData(KEYS.SHOW_SLIDE_COVER, val);
}

export function setIsCoverApplied(val: boolean) {
    store.setData(KEYS.IS_COVER_APPLIED, val);
}

// 评论失败，需刷新页面
export function setSaveCommentReload(val: boolean) {
    store.setData(KEYS.SAVECOMMENT_RELOAD, val);
}

export function setKmHeadTipsInfo(val: any) {
    store.setData(KEYS.KM_HEAD_TIPS_INFO, val);
}

export function setCurDocCardLikesInfo(val: ICurDocCardLikesInfo) {
    store.setData(KEYS.CUR_DOC_CARD_LIKES_INFO, val);
}

export function getCurDocCardLikesInfo() {
    return store.getData(KEYS.CUR_DOC_CARD_LIKES_INFO);
}

export function setIsShowCardLikeNewGuid(val: boolean) {
    store.setData(KEYS.IS_SHOW_CARD_LIKE_NEWGUID, val);
}

export function setUrlQuerySearch(val: any) {
    store.setData(KEYS.URL_QUERY_SEARCH, val);
}

export function getUrlQuerySearch() {
    return store.getData(KEYS.URL_QUERY_SEARCH);
}

export function setMaterialPosData(val: IMaterialPosData) {
    store.setData(KEYS.MATERIAL_POS_DATA, val);
}

/**
 * 设置更多菜单工具列表（同时支持增量和全量）
 * PS：工具包括内置和动态增加
 * @param list 可以是全量的工具列表，也可以是增量的列表
 */
export function setDropMenuToolList(list: IDropMenuToolItem[], sync = false) {
    if (!Array.isArray(list)) {
        return;
    }

    const newList: IDropMenuToolItem[] = [];
    list.forEach((item) => {
        if (newList.every((n) => n.id !== item.id)) {
            newList.push(item);
        }
    });

    store.setData(KEYS.DROP_MENU_TOOL_LIST, newList, sync);
}

export function setIsDocEmptyWithSpace(val: boolean) {
    store.setData(KEYS.IS_DOC_EMPTY_WITH_SPACE, val);
}

export function getIsDocEmptyWithSpace() {
    return store.getData(KEYS.IS_DOC_EMPTY_WITH_SPACE);
}

export function setDocWordNum(val: {
    totalWordCount: number;
    selectedWordCount?: number;
} | null) {
    store.setData(KEYS.DOC_WORD_NUM, val);
}

export function getDocWordNum() {
    return store.getData(KEYS.DOC_WORD_NUM);
}
export function getSDKShowSideBarTools() {
    // 只有在sdk页面才会被store控制，普通知识库页面 直接展示
    return (window as any).isSdkPage ? store.getData(KEYS.SDK_SHOW_SIDEBAR_TOOLS) : true;
}

export function setSDKShowSideBarTools(val: boolean) {
    store.setData(KEYS.SDK_SHOW_SIDEBAR_TOOLS, val);
}
export function getSDKShowAIPanel() {
    // 只有在sdk页面才会被store控制，普通知识库页面 直接展示
    return (window as any).isSdkPage ? store.getData(KEYS.SDK_SHOW_AI_PANEL) : true;
}

export function setSDKShowAIPanel(val: boolean) {
    store.setData(KEYS.SDK_SHOW_AI_PANEL, val);
}

export function getSDKShowDocumentTitle() {
    // 只有在sdk页面才会被store控制，普通知识库页面 直接展示
    return (window as any).isSdkPage ? store.getData(KEYS.SDK_SHOW_DOCUMENT_TITLE) : true;
}

export function setSDKShowDocumentTitle(val: boolean) {
    store.setData(KEYS.SDK_SHOW_DOCUMENT_TITLE, val);
}

export function getSDKShowDocComment() {
    // 只有在sdk页面才会被store控制，普通知识库页面 直接展示
    return (window as any).isSdkPage ? store.getData(KEYS.SDK_SHOW_DOCUMENT_COMMENT) : true;
}

export function setSDKShowDocComment(val: boolean) {
    store.setData(KEYS.SDK_SHOW_DOCUMENT_COMMENT, val);
}

export function setIsEditorReloading(val: boolean) {
    store.setData(KEYS.IS_EDITOR_RELOADING, val);
}
export function setShowPowerSetting(val: boolean) {
    store.setData(KEYS.SHOW_POWER_SETTING, val);
}
export function setShowMemberList(val: boolean) {
    store.setData(KEYS.SHOW_MEMBER_LIST, val);
}
export function setPowerModal(props: {docGuid: string, moreEntry: boolean}) {
    store.setData(KEYS.POWER_MODAL, props);
}
export function getPowerModal() {
    return store.getData(KEYS.POWER_MODAL);
}
export function setApplyAdminInfo(val: any) {
    store.setData(KEYS.APPLY_ADMIN_INFO, val);
}

export function setDocumentShareModalVisible(val: boolean) {
    store.setData(KEYS.DOCUMENT_SHARE_MODAL_VISIBLE, val);
}
export function setSidebarSelectBatch(val: boolean) {
    store.setData(KEYS.IS_TREE_BATCH_OPERATE, val);
}

export function getSidebarSelectBatch() {
    return store.getData(KEYS.IS_TREE_BATCH_OPERATE);
}

export function setTreeSelectedObj(val: {
    selectedCount: number; // 选中文档总数
    halfCheckedKeys: string[]; // 半选的文档id列表
    checkedKeys: string[]; // 全选的文档id列表
    filterCheckedKeys: string[]; // 过滤后的全选文档id列表 - 去掉父文档状态是全选的子文档id，用于后端传参
}) {
    store.setData(KEYS.TREE_SELECTED_OBJ, val);
}

export function getTreeSelectedObj() {
    return store.getData(KEYS.TREE_SELECTED_OBJ);
}

export function setTreeSelectedFull(val: boolean) {
    store.setData(KEYS.TREE_SELECTED_FULL, val);
}

export function getTreeSelectedFull() {
    return store.getData(KEYS.TREE_SELECTED_FULL);
}

export function setIsShowSidebar(val: boolean) {
    store.setData(KEYS.IS_SHOW_SIDEBAR, val);
}
export function setTreeData(val: any) {
    store.setData(KEYS.TREE_DATA, val);
}

export function setTreeLoaded(val: boolean) {
    store.setData(KEYS.IS_TREE_LOADED, val);
}

export function getTreeLoaded() {
    return store.getData(KEYS.IS_TREE_LOADED);
}

export function getTreeData() {
    return store.getData(KEYS.TREE_DATA);
}
export function setExternalRepoTips(val: any) {
    store.setData(KEYS.EXTERNAL_REPO_TIPS, val);
}
export function getExternalRepoTips() {
    return store.getData(KEYS.EXTERNAL_REPO_TIPS);
}

export function getFlexBuilderState() {
    return store.getData(KEYS.FLEX_BUILDER_STATE);
}
export function setFlexBuilderState(val: any) {
    return store.setData(KEYS.FLEX_BUILDER_STATE, val);
}

export function setDownloadFileStatus(status: 'idle'|'start') {
    store.setData(KEYS.DOWNLOAD_FILE_STATUS, status);
}

export function getDownloadFileStatus() {
    return store.getData(KEYS.DOWNLOAD_FILE_STATUS) || 'idle';
}
